import { useEffect, useRef, useState } from "react";
import { flush, track, trackOnce } from "../track";
import type { Words } from "./copy";
import { KIND_KEY } from "./kinds";
import type { OtherPreview } from "./otherPreviews";

/** "Your previews": every preview this person asked for, side by side under
 *  the heading, with the one they are on marked and each of the others one tap
 *  away.
 *
 *  Many ask for more than one, straight after the first ("Another preview for
 *  someone else" on the thank you page), and each is a page of its own. Without
 *  this the second page was the only one in sight, and the first could be found
 *  again only in their inbox.
 *
 *  Only previews this browser has opened and the server says are theirs (see
 *  otherPreviews.ts), so it is never on a page somebody was forwarded. Each
 *  tile is a plain link, so the page it opens is read from the start like any
 *  other: the page holds everything it knows against its own link, and a page
 *  swapped in underneath it would carry this one's payment and count no visit
 *  to the new one. */
export default function Others({ previews, words: t }: { previews: OtherPreview[]; words: Words }) {
  const strip = useRef<HTMLDivElement>(null);
  const here = useRef<HTMLDivElement>(null);
  const pictures = useAfterHero();

  // Once for the strip, however often the page reads it again: the page
  // starts its tracking afresh on every read, which forgets what was sent once.
  const shown = useRef(false);
  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    trackOnce("others.shown", {
      n: previews.length,
      ordered: previews.filter((p) => p.ordered).length,
      drawing: previews.filter((p) => p.state === "pending").length,
    });
  }, [previews]);

  // The one they are on, in view: it can be any of them, and the strip opens
  // at the first. Scrolled within the strip only, never the page.
  useEffect(() => {
    const row = strip.current;
    const tile = here.current;
    if (!row || !tile) return;
    row.scrollLeft = Math.max(0, tile.offsetLeft - row.offsetLeft - (row.clientWidth - tile.clientWidth) / 2);
  }, [previews.length]);

  return (
    <section className="others" aria-label={t.t("othersTitle", { n: String(previews.length) })}>
      <p className="others-title tiny">{t.t("othersTitle", { n: String(previews.length) })}</p>
      <div className="others-strip" ref={strip}>
        {previews.map((p) => {
          // What it is, then where it has got to: the one they are on, paid
          // for, or still being drawn. Two short lines rather than a label on
          // the picture, which no language but English fitted on.
          const what = p.pieces > 1 ? t.t("othersPieces", { n: String(p.pieces) }) : t.t(KIND_KEY[p.kind] ?? "kindMinime");
          const where = p.current
            ? t.t("othersThis")
            : p.ordered
              ? t.t("othersOrdered")
              : p.state === "pending"
                ? t.t("othersDrawing")
                : "";
          const tone = p.current ? "others-where-here" : p.ordered ? "others-where-ordered" : "others-where-drawing";
          const face = (
            <>
              <span className={p.state === "pending" ? "others-shot others-shot-drawing" : "others-shot"}>
                {p.thumb && (
                  <img
                    src={pictures ? p.thumb : undefined}
                    alt=""
                    width={84}
                    height={126}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                  />
                )}
                {p.ordered && (
                  <span className="others-tick" aria-hidden="true">
                    ✓
                  </span>
                )}
              </span>
              <span className="others-label">{what}</span>
              {where && <span className={`others-where ${tone}`}>{where}</span>}
            </>
          );
          return p.current ? (
            <div key={p.token} ref={here} className="others-tile others-tile-current" aria-current="page">
              {face}
            </div>
          ) : (
            <a
              key={p.token}
              className="others-tile"
              href={`/p/${p.token}`}
              aria-label={where ? `${what} · ${where}` : what}
              onClick={() => {
                track("others.opened", { state: p.state, ordered: p.ordered });
                // The page is leaving: what it has to say goes now.
                flush();
              }}
            >
              {face}
            </a>
          );
        })}
      </div>
    </section>
  );
}

/** Whether the strip's pictures may start loading. The drawings are full-size
 *  images and the strip sits above the one they came to see, so it waits until
 *  that one has arrived, or a moment and a half, whichever is first. */
function useAfterHero(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const hero = document.querySelector<HTMLImageElement>(".hero-img");
    if (!hero || hero.complete) {
      setReady(true);
      return;
    }
    const go = () => setReady(true);
    hero.addEventListener("load", go, { once: true });
    hero.addEventListener("error", go, { once: true });
    const timer = window.setTimeout(go, HERO_WAIT_MS);
    return () => {
      hero.removeEventListener("load", go);
      hero.removeEventListener("error", go);
      window.clearTimeout(timer);
    };
  }, []);
  return ready;
}

const HERO_WAIT_MS = 1500;
